/**
 * מאזן wizard — one side (צד א / צד ב): clearing-house upload, identity, and the
 * fund / balance / capital rows. The upload is parsed IN THE BROWSER; the raw
 * file is never sent anywhere.
 */

import { useRef, useState, type DragEvent } from 'react'
import { FileUp, Plus, Trash2, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react'
import { CheckField, ChoiceButtons, SelectField, TextField } from './fields'
import { filesToResults, mergeMaslaka, OCR_NOTE, type NonOriginal } from '../maslaka'
import { buildIntake, ils, noRightsSide, type Capital, type IntakeState, type MazanInputs, type Person, type RowIssue, type RowSection } from '../model'

interface Props {
  index: 0 | 1
  inputs: MazanInputs
  intake?: IntakeState
  invalidId?: string
  errorId: string
  /** incomplete rows to highlight (shown after a failed "הבא") */
  issues?: RowIssue[]
  onPerson: (p: Person) => void
  onIntake: (intake: IntakeState | undefined) => void
}

const num = (v: string) => {
  const n = parseFloat(v)
  return isFinite(n) && n >= 0 ? n : 0
}

export default function PersonStep({ index: i, inputs, intake, invalidId, errorId, issues = [], onPerson, onIntake }: Props) {
  const missingIn = (s: RowSection) => new Map(issues.filter((x) => x.section === s).map((x) => [x.index, x.missing]))
  const p = inputs.people[i]
  const tag = i === 0 ? 'צד א׳' : 'צד ב׳'
  const noRights = noRightsSide(inputs.rightsHolder, i)
  const fileRef = useRef<HTMLInputElement>(null)
  const [reading, setReading] = useState(false)
  const [readStatus, setReadStatus] = useState('')
  const [dragOver, setDragOver] = useState(false)
  // A non-original file (re-printed / re-saved / scanned) waits for the customer's choice.
  const [pendingNonOriginal, setPendingNonOriginal] = useState<{ files: File[]; items: NonOriginal[] } | null>(null)
  const set = (patch: Partial<Person>) => onPerson({ ...p, ...patch })

  const readFiles = async (files: File[], acceptNonOriginal = false) => {
    if (!files.length) return
    setReading(true)
    setReadStatus('קורא את הקבצים…')
    try {
      const merged = mergeMaslaka(await filesToResults(files, { onProgress: setReadStatus, acceptNonOriginal }))
      if (merged.pendingNonOriginal) {
        setPendingNonOriginal({ files, items: merged.nonOriginal })
        setReadStatus('הקובץ שהועלה אינו הקובץ המקורי מהמסלקה — בחרו כיצד להמשיך.')
        setTimeout(() => document.getElementById(`p${i}-nonoriginal`)?.focus(), 0)
        return
      }
      setPendingNonOriginal(null)
      const { intake: next, person } = buildIntake(merged, inputs.breakDate)
      const who = merged.person
      onPerson({
        ...p,
        // The PDF has no birth date / gender — never keep stale values silently.
        name: who?.name || p.name,
        idNumber: who?.id || p.idNumber,
        birth: who ? who.birth || '' : p.birth,
        gender: who ? who.gender || '' : p.gender,
        ...person,
      })
      onIntake(next)
      const got = merged.funds.length + merged.capital.length
      setReadStatus(
        merged.errors.length && !got
          ? `לא ניתן היה לקרוא את הקבצים: ${merged.errors.map((e) => e.msg).join(' · ')}`
          : `נקראו ${merged.funds.length} קרנות פנסיה ו־${merged.capital.length} נכסים הוניים מתוך ${merged.fileCount} קבצים.` +
              (who && (!who.birth || !who.gender) ? ' דוח PDF אינו כולל תאריך לידה ומין — יש להשלים ידנית.' : '') +
              (merged.ocr ? ' הדוח נקרא בזיהוי תווים — יש לבדוק את הנתונים.' : '')
      )
    } catch {
      setReadStatus('אירעה שגיאה בקריאת הקבצים. אפשר להזין את הנתונים ידנית.')
    } finally {
      setReading(false)
    }
  }

  /** "Continue anyway with the current report" (or undo it) — rebuilds the rows. */
  const setAcceptUnadjusted = (accept: boolean) => {
    if (!intake) return
    const { intake: next, person } = buildIntake(intake.merged, inputs.breakDate, accept)
    onPerson({ ...p, ...person })
    onIntake(next)
    setReadStatus(accept
      ? 'ממשיכים לפי הדוח הנוכחי, ללא התאמה למועד הקרע. הדבר יצוין בחוות הדעת.'
      : 'בוטל. יש להעלות דוח המכסה את מועד הקרע, או לבחור שוב להמשיך לפי הדוח הנוכחי.')
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    readFiles(Array.from(e.dataTransfer.files))
  }

  const fieldId = (k: string) => `p${i}-${k}`

  return (
    <div className="space-y-6">
      {!noRights && (
        <section aria-labelledby={`${fieldId('upload')}-title`}
          className={`rounded-xl border-2 border-dashed p-5 text-center ${dragOver ? 'border-emerald-700 bg-emerald-50' : 'border-gray-300 bg-white'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <h3 id={`${fieldId('upload')}-title`} className="text-base font-semibold text-gray-900">דוחות פנסיוניים — {tag}</h3>
          <p className="mx-auto mt-1 max-w-lg text-sm text-gray-700">
            העלו את דוח המסלקה הפנסיונית (PDF, קובצי XML או ה־ZIP כולו), ו/או דוח שנתי או רבעוני מקרן הפנסיה, קופת הגמל, קרן ההשתלמות
            או ביטוח המנהלים (PDF). סוג הקופה, הצבירה, הקצבה החודשית ותאריך הדוח ייקראו מהקובץ ויושוו למועד הקרע.
            הקובץ נקרא במכשיר שלכם בלבד ואינו נשלח לשרת.
          </p>
          <button type="button" className="btn btn-outline mt-3 inline-flex items-center gap-2" disabled={reading} onClick={() => fileRef.current?.click()}>
            <FileUp size={18} aria-hidden="true" />
            {reading ? 'קורא…' : 'בחירת קבצים'}
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            accept=".pdf,.xml,.zip,application/pdf,application/xml,text/xml,application/zip"
            onChange={(e) => { readFiles(Array.from(e.target.files || [])); e.target.value = '' }}
          />
          <p role="status" aria-live="polite" className="mt-2 text-sm text-gray-800">{readStatus}</p>
          {pendingNonOriginal && (
            <NonOriginalPanel
              panelId={`p${i}-nonoriginal`}
              items={pendingNonOriginal.items}
              reading={reading}
              onContinue={() => readFiles(pendingNonOriginal.files, true)}
              onUpload={() => fileRef.current?.click()}
            />
          )}
          {intake && !pendingNonOriginal && (
            <IntakeSummary
              panelId={`p${i}-unadjusted`}
              intake={intake}
              reading={reading}
              onAccept={() => setAcceptUnadjusted(true)}
              onUndo={() => setAcceptUnadjusted(false)}
              onUpload={() => fileRef.current?.click()}
            />
          )}
        </section>
      )}

      <section aria-labelledby={`${fieldId('id')}-title`} className="card">
        <h3 id={`${fieldId('id')}-title`} className="mb-3 text-base font-semibold text-gray-900">פרטים אישיים — {tag}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField id={fieldId('name')} label="שם מלא" className="sm:col-span-2" value={p.name} invalidId={invalidId} errorId={errorId}
            hint="כפי שמופיע בדוח המסלקה — שם משפחה ואחריו שם פרטי" onChange={(v) => set({ name: v })} />
          <SelectField id={fieldId('gender')} label="מין" value={p.gender} invalidId={invalidId} errorId={errorId}
            hint="קובע גיל פרישה ותוחלת חיים" onChange={(v) => set({ gender: v as Person['gender'] })}
            options={[{ value: '', label: '— יש לבחור —' }, { value: 'male', label: 'גבר' }, { value: 'female', label: 'אישה' }]} />
          <TextField id={fieldId('birth')} label="תאריך לידה" type="date" value={p.birth} invalidId={invalidId} errorId={errorId} onChange={(v) => set({ birth: v })} />
          <TextField id={fieldId('id')} label="מספר תעודת זהות" inputMode="numeric" dir="ltr" value={p.idNumber || ''} invalidId={invalidId} errorId={errorId}
            hint="נדרש לפסיקתא" onChange={(v) => set({ idNumber: v.replace(/\D/g, '').slice(0, 9) })} />
        </div>
        {noRights && (
          <p className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-700">
            אין לצד זה זכויות פנסיוניות. תאריך הלידה עדיין נדרש — הוא קובע את תרחיש התמותה המוקדמת.
          </p>
        )}
      </section>

      {!noRights && (
        <>
          <RowsSection
            title="קופות פנסיה — אלה שמקבלות פסיקתא"
            note="מדוח המסלקה: הקצבה החודשית לגיל פרישה ללא המשך הפקדות — לא זו שכוללת המשך הפקדות, ולא היתרה הצבורה."
            addLabel="הוספת קופה"
            rows={p.funds}
            missing={missingIn('funds')}
            onAdd={() => set({ funds: [...p.funds, { name: '', amount: 0 }] })}
            onRemove={(j) => set({ funds: p.funds.filter((_, k) => k !== j) })}
            render={(f, j) => {
              const upd = (patch: Partial<typeof f>) => set({ funds: p.funds.map((x, k) => (k === j ? { ...x, ...patch } : x)) })
              const id = (k: string) => `p${i}-f${j}-${k}`
              return (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextField id={id('name')} label="שם הקופה" value={f.name} onChange={(v) => upd({ name: v })} />
                  <TextField id={id('acct')} label="מס׳ חשבון" dir="ltr" value={f.acct || ''} onChange={(v) => upd({ acct: v })} />
                  <TextField id={id('amount')} label="קצבה חודשית צפויה בגיל פרישה (₪)" type="number" min={0} step={1} value={f.amount || ''} onChange={(v) => upd({ amount: num(v) })} />
                  <TextField id={id('opened')} label="מועד פתיחת הקופה" type="date" value={f.opened || ''} onChange={(v) => upd({ opened: v })} />
                  <TextField id={id('body')} label="גוף משלם (לכבוד)" value={f.body || ''} onChange={(v) => upd({ body: v })} />
                  <TextField id={id('nikuim')} label="תיק ניכויים" dir="ltr" value={f.nikuim || ''} onChange={(v) => upd({ nikuim: v })} />
                </div>
              )
            }}
            rowLabel={(f, j) => f.name || `קופה ${j + 1}`}
          />

          <RowsSection
            title="יתרות צבורות"
            note="מדוח המסלקה: סה״כ חיסכון צבור בקופות פנסיה ובגמל פעיל. לא קרן השתלמות ולא גמל להשקעה."
            addLabel="הוספת יתרה"
            rows={p.balances}
            missing={missingIn('balances')}
            onAdd={() => set({ balances: [...p.balances, { name: '', amount: 0 }] })}
            onRemove={(j) => set({ balances: p.balances.filter((_, k) => k !== j) })}
            render={(b, j) => {
              const upd = (patch: Partial<typeof b>) => set({ balances: p.balances.map((x, k) => (k === j ? { ...x, ...patch } : x)) })
              return (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextField id={`p${i}-b${j}-name`} label="שם הנכס" value={b.name} onChange={(v) => upd({ name: v })} />
                  <TextField id={`p${i}-b${j}-amount`} label="יתרה צבורה למועד הקרע (₪)" type="number" min={0} step={1} value={b.amount || ''} onChange={(v) => upd({ amount: num(v) })} />
                </div>
              )
            }}
            rowLabel={(b, j) => b.name || `יתרה ${j + 1}`}
          />

          <RowsSection
            title="נכסים הוניים — השתלמות, גמל להשקעה, ביטוח מנהלים"
            note="מצב המס של כל נכס נקבע לפי דוח המסלקה: קרן השתלמות נזילה פטורה ממס, קופה לא פעילה מהוונת מגיל פרישה, וכל השאר — 25% מס. שאלה תופיע רק כשהדוח אינו מציין זאת."
            addLabel="הוספת נכס הוני"
            rows={p.capital}
            missing={missingIn('capital')}
            onAdd={() => set({ capital: [...p.capital, { name: '', amount: 0 }] })}
            onRemove={(j) => set({ capital: p.capital.filter((_, k) => k !== j) })}
            render={(c, j) => {
              const upd = (patch: Partial<typeof c>) => set({ capital: p.capital.map((x, k) => (k === j ? { ...x, ...patch } : x)) })
              const id = (k: string) => `p${i}-c${j}-${k}`
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <TextField id={id('name')} label="שם הנכס" value={c.name} onChange={(v) => upd({ name: v })} />
                    <TextField id={id('amount')} label={c.atRetire ? 'סכום חד־פעמי צפוי בגיל פרישה (₪)' : 'יתרה למועד הקרע (₪)'}
                      type="number" min={0} step={1} value={c.amount || ''} onChange={(v) => upd({ amount: num(v) })} />
                    <TextField id={id('opened')} label="מועד פתיחת הנכס" type="date" value={c.opened || ''} onChange={(v) => upd({ opened: v })} />
                  </div>
                  <CapitalTax idPrefix={id('')} c={c} breakDate={inputs.breakDate} onChange={upd} />
                </div>
              )
            }}
            rowLabel={(c, j) => c.name || `נכס ${j + 1}`}
          />
        </>
      )}
    </div>
  )
}

/**
 * The uploaded file is not the clearing-house original. Same two-choice card as
 * the "doesn't match the break date" one: continue with this file (prominent),
 * or upload the original.
 */
export function NonOriginalPanel({ panelId, items, reading, onContinue, onUpload }: {
  panelId: string
  items: NonOriginal[]
  reading: boolean
  onContinue: () => void
  onUpload: () => void
}) {
  const ocr = items.some((n) => n.noText)
  return (
    <div id={panelId} tabIndex={-1} role="alert" className="mt-4 rounded-xl border-2 border-amber-500 bg-amber-50 p-4 text-start text-sm text-amber-950 focus:outline-none">
      <p className="flex items-start gap-2 text-base font-semibold">
        <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span>הקובץ שהועלה אינו הקובץ המקורי מהמסלקה הפנסיונית</span>
      </p>
      <ul className="mt-2 space-y-1">
        {items.map((n) => (
          <li key={n.file}>
            <strong>{n.file}</strong>: {n.reason}.
            {n.producer && <span className="block text-xs text-amber-900">יוצר הקובץ, כפי שמופיע בו: <span dir="ltr">{n.producer}</span></span>}
          </li>
        ))}
      </ul>
      <p className="mt-3 font-semibold">כדי להמשיך, בחרו אחת משתי האפשרויות:</p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col rounded-lg border-2 border-primary-600 bg-white p-3 text-gray-900">
          <p className="font-semibold">1. להמשיך עם הקובץ הנוכחי</p>
          <p className="mt-1 flex-1 text-xs text-gray-700">
            {items[0]?.detail} הדבר יצוין בחוות הדעת.
          </p>
          <button type="button" className="btn btn-primary mt-3 w-full gap-2 py-3 text-base shadow-md" disabled={reading} onClick={onContinue}>
            {reading ? (ocr ? 'מזהה טקסט…' : 'קורא…') : 'המשך עם הקובץ הנוכחי'} <ArrowLeft size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="flex flex-col rounded-lg border border-gray-300 bg-white p-3 text-gray-900">
          <p className="font-semibold">2. להעלות את הקובץ המקורי (מדויק יותר)</p>
          <p className="mt-1 flex-1 text-xs text-gray-700">
            הקובץ כפי שהורד מאתר המסלקה הפנסיונית — PDF, או קובץ ה־ZIP עם קובצי ה־XML (כולל גם תאריך לידה ומין).
          </p>
          <button type="button" className="btn btn-outline mt-3 w-full gap-2 py-3" disabled={reading} onClick={onUpload}>
            <FileUp size={18} aria-hidden="true" /> העלאת הקובץ המקורי
          </button>
        </div>
      </div>
    </div>
  )
}

const dmy = (iso?: string) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : '—')

function IntakeSummary({ panelId, intake, reading, onAccept, onUndo, onUpload }: {
  panelId: string
  intake: IntakeState
  reading: boolean
  onAccept: () => void
  onUndo: () => void
  onUpload: () => void
}) {
  const items = [...intake.funds, ...intake.capital]
  if (!items.length && !intake.merged.skipped.length) return null
  const problems = items.filter((f) => f.adjustmentStatus !== 'ok')
  const asOfs = [...new Set(problems.map((f) => dmy(f.asOf)))].join(', ')
  return (
    <div className="mt-4 text-start">
      {intake.merged.nonOriginal.length > 0 && (
        <div role="status" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <p>
            <strong>ממשיכים עם קובץ שאינו המקורי:</strong> {intake.merged.nonOriginal.map((n) => n.reason).join(' · ')}. הדבר יצוין בחוות הדעת.
          </p>
        </div>
      )}
      {intake.merged.ocr && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <p className="font-semibold">הדוח נקרא בזיהוי תווים (OCR)</p>
          <p className="mt-1">{OCR_NOTE}</p>
        </div>
      )}
      {intake.hasProblem && !intake.acceptedUnadjusted && (
        <div id={panelId} tabIndex={-1} role="alert" className="mb-4 rounded-xl border-2 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950 focus:outline-none">
          <p className="flex items-start gap-2 text-base font-semibold">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              {problems.length === items.length ? 'הדוח' : `${problems.length} מתוך ${items.length} הקופות בדוח`} אינו מתאים למועד הקרע: {problems[0]?.adjustmentMessage}
            </span>
          </p>
          <p className="mt-3 font-semibold">כדי להמשיך לשלב הבא, בחרו אחת משתי האפשרויות:</p>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col rounded-lg border-2 border-primary-600 bg-white p-3 text-gray-900">
              <p className="font-semibold">1. להמשיך עם הדוח הנוכחי</p>
              <p className="mt-1 flex-1 text-xs text-gray-700">
                התחשיב ייערך לפי היתרות והקצבאות שבדוח (נכון ל־{asOfs}), <strong>ללא התאמה למועד הקרע</strong>. הדבר יצוין בחוות הדעת.
              </p>
              <button type="button" className="btn btn-primary mt-3 w-full gap-2 py-3 text-base shadow-md" onClick={onAccept}>
                המשך בכל זאת לפי הדוח הנוכחי <ArrowLeft size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="flex flex-col rounded-lg border border-gray-300 bg-white p-3 text-gray-900">
              <p className="font-semibold">2. להעלות דוח מתאים (מדויק יותר)</p>
              <p className="mt-1 flex-1 text-xs text-gray-700">דוח מסלקה, או דוח שנתי / רבעוני מהקופה, שמכסה את מועד הקרע — עד שישה חודשים אחריו.</p>
              <button type="button" className="btn btn-outline mt-3 w-full gap-2 py-3" disabled={reading} onClick={onUpload}>
                <FileUp size={18} aria-hidden="true" /> {reading ? 'קורא…' : 'העלאת דוח אחר'}
              </button>
            </div>
          </div>
        </div>
      )}
      {intake.acceptedUnadjusted && (
        <div role="status" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <p>
            <strong>ממשיכים לפי הדוח הנוכחי.</strong> {problems.length} קופות יחושבו לפי הדוח (נכון ל־{asOfs}), ללא התאמה למועד הקרע — הדבר יצוין
            בחוות הדעת.
          </p>
          <button type="button" className="mt-2 font-medium underline" onClick={onUndo}>ביטול — אעלה דוח מתאים</button>
        </div>
      )}
      <ul className="space-y-2 text-sm">
        {items.map((f, k) => (
          <li key={k} className={`flex items-start gap-2 rounded-lg p-2 ${f.adjustmentStatus === 'ok' ? 'bg-emerald-50 text-emerald-950' : intake.acceptedUnadjusted ? 'bg-amber-50 text-amber-950' : 'bg-red-50 text-red-900'}`}>
            {f.adjustmentStatus === 'ok'
              ? <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
              : <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />}
            <span>
              <strong>{f.plan}</strong> · {ils.format(f.balance)}{f.kitzba ? ` · ${ils.format(f.kitzba)} לחודש` : ''}
              <span className="block text-xs">
                {f.adjustmentStatus === 'ok' ? '' : intake.acceptedUnadjusted ? 'לפי הדוח, ללא התאמה למועד הקרע: ' : 'לא ניתן להתאים: '}
                {f.adjustmentMessage}
              </span>
            </span>
          </li>
        ))}
        {intake.merged.skipped.map((s, k) => (
          <li key={`s${k}`} className="flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-amber-950">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span><strong>{s.plan || s.kind}</strong> — לא נכלל בתחשיב: {s.why}. לטיפול יש ליצור קשר עם יפתח.</span>
          </li>
        ))}
      </ul>
      {intake.merged.notes.map((n, k) => <p key={k} className="mt-2 text-xs text-amber-900">{n}</p>)}
      {intake.merged.errors.map((e, k) => <p key={`e${k}`} className="mt-2 text-xs text-red-800">{e.file}: {e.msg}</p>)}
    </div>
  )
}

/**
 * A capital row's tax status. Rows from the clearing-house report (kind set) show
 * the status the report determines, with the reason; when the report doesn't
 * say, ONE yes/no question. Rows typed by hand keep the two checkboxes.
 */
function CapitalTax({ idPrefix, c, breakDate, onChange }: {
  idPrefix: string
  c: Capital
  breakDate: string
  onChange: (patch: Partial<Capital>) => void
}) {
  const [manual, setManual] = useState(false)
  const boxes = (
    <div className="space-y-2">
      <CheckField id={`${idPrefix}liquid`} checked={!!c.liquid}
        onChange={(v) => onChange({ liquid: v, atRetire: v ? false : c.atRetire, overridden: !!c.kind })}
        label="קרן השתלמות שניתן כבר לפדות — פטורה ממס" />
      <CheckField id={`${idPrefix}atRetire`} checked={!!c.atRetire}
        onChange={(v) => onChange({ atRetire: v, liquid: v ? false : c.liquid, overridden: !!c.kind })}
        label="פנסיה לא פעילה — סכום חד־פעמי שיתקבל בגיל פרישה (יהוון למועד הקרע)" />
    </div>
  )
  if (!c.kind) return boxes

  if (c.ask) {
    const brk = breakDate && /^\d{4}-\d{2}-\d{2}$/.test(breakDate) ? breakDate.split('-').reverse().join('/') : 'מועד הקרע'
    const question = c.ask === 'liquid'
      ? `האם ב־${brk} כבר ניתן היה למשוך את קרן ההשתלמות בפטור ממס (עברו 6 שנים מההצטרפות לראשונה)?`
      : 'הכסף בקופה יתקבל כסכום חד־פעמי רק בגיל הפרישה?'
    const yes = c.answered ? (c.ask === 'liquid' ? !!c.liquid : !!c.atRetire) : null
    return (
      <div className={`rounded-lg border p-3 text-sm ${c.answered ? 'border-gray-200 bg-gray-50' : 'border-amber-400 bg-amber-50'}`}>
        <p className="text-gray-800">{c.basis}.</p>
        <ChoiceButtons
          legend={question}
          idPrefix={`${idPrefix}ask`}
          value={yes}
          options={[{ value: true, label: 'כן', id: `${idPrefix}ask-yes` }, { value: false, label: 'לא', id: `${idPrefix}ask-no` }]}
          onChange={(v) => onChange(c.ask === 'liquid'
            ? { answered: true, liquid: v, atRetire: false }
            : { answered: true, atRetire: v, liquid: false })}
        />
        {c.ask === 'atRetire' && yes && (
          <p className="mt-2 text-xs text-gray-700">יש להזין למעלה את הסכום החד־פעמי הצפוי בגיל פרישה (מהדוח השנתי של הקופה).</p>
        )}
      </div>
    )
  }

  const tone = c.liquid ? 'border-emerald-300 bg-emerald-50 text-emerald-950'
    : c.atRetire ? 'border-sky-300 bg-sky-50 text-sky-950' : 'border-gray-200 bg-gray-50 text-gray-900'
  const title = c.liquid ? 'פטורה ממס' : c.atRetire ? 'פנסיה לא פעילה — מהוונת למועד הקרע' : 'חייב במס 25%'
  return (
    <div className={`rounded-lg border p-3 text-sm ${tone}`}>
      <p className="flex items-start gap-2">
        <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span><strong>{title}</strong>{c.overridden ? ' (שונה ידנית)' : ''} — {c.basis}</span>
      </p>
      {manual ? <div className="mt-2">{boxes}</div> : (
        <button type="button" className="mt-1 text-xs underline" onClick={() => setManual(true)}>הסיווג שגוי? שינוי ידני</button>
      )}
    </div>
  )
}

function RowsSection<T>({
  title, note, addLabel, rows, missing, onAdd, onRemove, render, rowLabel,
}: {
  title: string
  note: string
  addLabel: string
  rows: T[]
  /** row index → missing field names (highlighted after a failed "הבא") */
  missing: Map<number, string[]>
  onAdd: () => void
  onRemove: (j: number) => void
  render: (row: T, j: number) => JSX.Element
  rowLabel: (row: T, j: number) => string
}) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  return (
    <section className="card" aria-label={title}>
      <h3 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-gray-900 focus:outline-none">{title}</h3>
      <p className="mt-1 text-sm text-gray-700">{note}</p>
      <div className="mt-3 space-y-4">
        {rows.map((row, j) => {
          const miss = missing.get(j)
          return (
          <div key={j} className={`rounded-lg p-3 ${miss ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-200'}`}>
            {miss && (
              <p className="mb-2 flex items-start gap-1 text-sm font-semibold text-red-800">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                חסר: {miss.join(', ')} — יש להשלים או להסיר את השורה
              </p>
            )}
            {render(row, j)}
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-sm text-red-800 underline"
              aria-label={`הסרת ${rowLabel(row, j)}`}
              onClick={() => { onRemove(j); headingRef.current?.focus() }}
            >
              <Trash2 size={14} aria-hidden="true" /> הסרה
            </button>
          </div>
          )
        })}
      </div>
      <button type="button" className="btn btn-secondary mt-3 inline-flex items-center gap-1" onClick={onAdd}>
        <Plus size={16} aria-hidden="true" /> {addLabel}
      </button>
    </section>
  )
}
